import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiClient } from '../lib/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import NMRecordEditor from '../components/NMRecordEditor';
import {
    Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight,
    Loader2, Database, Hash, FileText, Tag, X, Check, AlertTriangle,
    ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Upload, Download,
    ChevronDown, ChevronUp, CheckSquare, Square, MoreHorizontal
} from 'lucide-react';

function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => { const t = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return debounced;
}

export default function NounModifierPage() {
    // ── Form ─────────────────────────────────────────────────
    const [form, setForm] = useState({
        partNumbers: [],       // Multiple PNs
        descriptions: [],      // Multiple Descriptions
        nounModifier: ''        // Single
    });
    const [pnInput, setPnInput] = useState('');         // Current PN input text
    const [descInput, setDescInput] = useState('');     // Current Description input text
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dupCheck, setDupCheck] = useState({ checking: false, duplicates: [] }); // Track which PNs are dupes
    const [formOpen, setFormOpen] = useState(false);
    const pnInputRef = useRef(null);
    const descInputRef = useRef(null);

    // ── Table ────────────────────────────────────────────────
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('id');
    const [sortDir, setSortDir] = useState('DESC');
    const [selectedIds, setSelectedIds] = useState(new Set());

    // ── Stats & modals ───────────────────────────────────────
    const [stats, setStats] = useState({ total: 0, totalItems: 0, uniqueNouns: 0 });
    const [deleteModal, setDeleteModal] = useState(null);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importProgress, setImportProgress] = useState(null);
    const fileInputRef = useRef(null);

    const debouncedSearch = useDebounce(searchTerm, 350);

    // ── Ref to avoid pagination in useCallback deps ──────────
    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;

    // ── Fetch ────────────────────────────────────────────────
    const fetchRecords = useCallback(async (page) => {
        const pag = paginationRef.current;
        const fetchPage = page != null ? page : pag.page;
        setLoading(true);
        try {
            const resp = await apiClient.nounModifierList({
                page: fetchPage, limit: pag.limit, search: debouncedSearch, sortBy, sortDir
            });
            if (resp.success) {
                setRecords(resp.data);
                setPagination(resp.pagination);
                setSelectedIds(new Set());
            }
        } catch (err) { toast.error('Failed to load records'); }
        finally { setLoading(false); }
    }, [debouncedSearch, sortBy, sortDir]);

    const fetchStats = useCallback(async () => {
        try {
            const r = await apiClient.nounModifierStats();
            if (r.success) setStats({ total: r.total, totalItems: r.totalItems || 0, uniqueNouns: r.uniqueNouns });
        } catch (_) { }
    }, []);

    useEffect(() => { fetchRecords(1); }, [fetchRecords]);
    useEffect(() => { fetchStats(); }, []);

    // ── Duplicate check (batch — check all PNs) ──────────────
    useEffect(() => {
        if (form.partNumbers.length === 0) {
            setDupCheck({ checking: false, duplicates: [] });
            return;
        }
        let cancelled = false;
        setDupCheck(d => ({ ...d, checking: true }));

        // Check each PN for duplicates
        const checkAll = async () => {
            const dupes = [];
            for (const pn of form.partNumbers) {
                try {
                    const r = await apiClient.nounModifierCheckDuplicate({
                        partNumber: pn.trim(),
                        excludeId: editingId
                    });
                    if (r.exists) dupes.push(pn);
                } catch (_) { }
            }
            if (!cancelled) setDupCheck({ checking: false, duplicates: dupes });
        };
        checkAll();
        return () => { cancelled = true; };
    }, [form.partNumbers, editingId]);

    // ── PN chip add/remove ───────────────────────────────────
    const addPartNumber = () => {
        const val = pnInput.trim().toUpperCase();
        if (!val) return;
        if (form.partNumbers.includes(val)) {
            toast.warning(`"${val}" is already added`);
            return;
        }
        setForm(f => ({ ...f, partNumbers: [...f.partNumbers, val] }));
        setPnInput('');
        setTimeout(() => pnInputRef.current?.focus(), 50);
    };

    // Bulk paste: supports newline, comma, tab, semicolon separated PNs
    const handlePnPaste = (e) => {
        const text = e.clipboardData?.getData('text') || '';
        const parts = text.split(/[\n\r,;\t]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
        if (parts.length > 1) {
            e.preventDefault();
            const existing = new Set(form.partNumbers);
            const newPNs = parts.filter(p => !existing.has(p));
            const dupes = parts.length - newPNs.length;
            setForm(f => ({ ...f, partNumbers: [...f.partNumbers, ...newPNs] }));
            setPnInput('');
            toast.success(`Added ${newPNs.length} part number(s)${dupes > 0 ? ` (${dupes} duplicates skipped)` : ''}`);
        }
    };

    const removePartNumber = (idx) => {
        setForm(f => ({ ...f, partNumbers: f.partNumbers.filter((_, i) => i !== idx) }));
    };

    const clearAllPNs = () => {
        setForm(f => ({ ...f, partNumbers: [] }));
        toast.info('All part numbers cleared');
    };

    const handlePnKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addPartNumber(); }
        if (e.key === 'Backspace' && !pnInput && form.partNumbers.length > 0) {
            removePartNumber(form.partNumbers.length - 1);
        }
    };

    // ── Description chip add/remove ──────────────────────────
    const addDescription = () => {
        const val = descInput.trim().toUpperCase();
        if (!val) return;
        setForm(f => ({ ...f, descriptions: [...f.descriptions, val] }));
        setDescInput('');
        setTimeout(() => descInputRef.current?.focus(), 50);
    };

    // Bulk paste for descriptions
    const handleDescPaste = (e) => {
        const text = e.clipboardData?.getData('text') || '';
        const parts = text.split(/[\n\r;\t]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
        if (parts.length > 1) {
            e.preventDefault();
            setForm(f => ({ ...f, descriptions: [...f.descriptions, ...parts] }));
            setDescInput('');
            toast.success(`Added ${parts.length} description(s)`);
        }
    };

    const removeDescription = (idx) => {
        setForm(f => ({ ...f, descriptions: f.descriptions.filter((_, i) => i !== idx) }));
    };

    const clearAllDescs = () => {
        setForm(f => ({ ...f, descriptions: [] }));
        toast.info('All descriptions cleared');
    };

    const handleDescKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addDescription(); }
        if (e.key === 'Backspace' && !descInput && form.descriptions.length > 0) {
            removeDescription(form.descriptions.length - 1);
        }
    };

    // ── Form handlers ────────────────────────────────────────
    const resetForm = () => {
        setForm({ partNumbers: [], descriptions: [], nounModifier: '' });
        setPnInput('');
        setDescInput('');
        setEditingId(null);
        setDupCheck({ checking: false, duplicates: [] });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.partNumbers.length === 0 || form.descriptions.length === 0 || !form.nounModifier.trim()) {
            toast.error('Part Numbers, Descriptions, and Noun/Modifier are required');
            return;
        }

        // Block if any duplicates exist (only for new records)
        if (!editingId && dupCheck.duplicates.length > 0) {
            toast.error(`Duplicate Part Number(s): ${dupCheck.duplicates.join(', ')}`);
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                await apiClient.nounModifierUpdate(editingId, {
                    partNumbers: form.partNumbers,
                    descriptions: form.descriptions,
                    nounModifier: form.nounModifier
                });
                toast.success('Updated');
            } else {
                const result = await apiClient.nounModifierCreate({
                    partNumbers: form.partNumbers,
                    descriptions: form.descriptions,
                    nounModifier: form.nounModifier
                });
                toast.success(`Created group with ${result.count} Part Number(s)`);
            }
            resetForm();
            setFormOpen(false);
            fetchRecords(editingId ? pagination.page : 1);
            fetchStats();
        } catch (err) {
            toast.error(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (group) => {
        setForm({
            partNumbers: (group.items || []).map(it => it.part_number),
            descriptions: (group.items || []).map(it => it.description || ''),
            nounModifier: group.noun_modifier || ''
        });
        setPnInput('');
        setDescInput('');
        setEditingId(group.id);
        setFormOpen(true);
        setTimeout(() => pnInputRef.current?.focus(), 200);
    };

    const handleDelete = async () => { if (!deleteModal) return; try { await apiClient.nounModifierDelete(deleteModal.id); toast.success('Deleted'); setDeleteModal(null); fetchRecords(); fetchStats(); } catch (err) { toast.error(err.message); } };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        const confirmed = window.confirm(`Are you sure you want to delete ${selectedIds.size} selected group(s)? This cannot be undone.`);
        if (!confirmed) return;
        try {
            const r = await apiClient.nounModifierBulkDelete([...selectedIds]);
            toast.success(`Deleted ${r.deleted} record(s)`);
            setSelectedIds(new Set()); fetchRecords(); fetchStats();
        } catch (err) { toast.error(err.message); }
    };

    // ── Sort ──────────────────────────────────────────────────
    const handleSort = (col) => { if (sortBy === col) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC'); else { setSortBy(col); setSortDir('ASC'); } };
    const SortIcon = ({ col }) => {
        if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
        return sortDir === 'ASC' ? <ArrowUp className="w-3 h-3 text-blue-600" /> : <ArrowDown className="w-3 h-3 text-blue-600" />;
    };

    // ── Select ───────────────────────────────────────────────
    const allSelected = records.length > 0 && records.every(r => selectedIds.has(r.id));
    const toggleAll = () => { if (allSelected) setSelectedIds(new Set()); else setSelectedIds(new Set(records.map(r => r.id))); };
    const toggleOne = (id) => { setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

    // ── Import ───────────────────────────────────────────────
    const handleImportClick = () => fileInputRef.current?.click();

    const runBulkImport = async (records, title = 'Importing Noun/Modifier data') => {
        const totals = { inserted: 0, updated: 0, skipped: 0, failed: 0 };
        const outcomes = [];
        setImporting(true);
        setImportProgress({ open: true, running: true, title, total: records.length, processed: 0, ...totals, outcomes, retryRecords: records });

        for (let start = 0; start < records.length; start += 200) {
            const batch = records.slice(start, start + 200);
            try {
                const result = await apiClient.nounModifierBulkImport(batch);
                totals.inserted += Number(result.inserted || 0);
                totals.updated += Number(result.updated || 0);
                totals.skipped += Number(result.skipped || 0);
                totals.failed += Array.isArray(result.errors) ? result.errors.length : 0;
                if (Array.isArray(result.outcomes)) outcomes.push(...result.outcomes);
                else (result.errors || []).forEach(error => outcomes.push({ row: error.row, status: 'failed', partNumber: error.partNumber, reason: error.error }));
            } catch (error) {
                totals.failed += batch.length;
                batch.forEach(record => outcomes.push({
                    row: record.__importRow, status: 'failed', partNumber: record.partNumber || record.part_number || '',
                    reason: error.message || 'Batch request failed'
                }));
            }
            setImportProgress(previous => ({ ...previous, processed: Math.min(start + batch.length, records.length), ...totals, outcomes: [...outcomes] }));
        }

        setImportResult({ inserted: totals.inserted, updated: totals.updated, skipped: totals.skipped, errors: outcomes.filter(item => item.status === 'failed') });
        setImportProgress(previous => ({ ...previous, running: false, processed: records.length, ...totals, outcomes: [...outcomes] }));
        setImporting(false);
        toast.success(`Import complete: ${(totals.inserted + totals.updated).toLocaleString()} imported, ${totals.skipped.toLocaleString()} skipped, ${totals.failed.toLocaleString()} failed`);
        fetchRecords(1);
        fetchStats();
        return { inserted: totals.inserted, updated: totals.updated, skipped: totals.skipped, errors: outcomes.filter(item => item.status === 'failed') };
    };

    const retryImportOutcomes = async (statuses) => {
        if (!importProgress || importProgress.running) return;
        const rows = new Set(importProgress.outcomes.filter(item => statuses.includes(item.status)).map(item => Number(item.row)));
        const retryRecords = (importProgress.retryRecords || []).filter(record => rows.has(Number(record.__importRow)));
        if (!retryRecords.length) return toast.info('There are no matching rows to retry');
        await runBulkImport(retryRecords, `Retrying ${retryRecords.length.toLocaleString()} row(s)`);
    };

    const exportImportReport = () => {
        if (!importProgress?.outcomes?.length) return toast.info('No import results to export');
        const sourceByRow = new Map((importProgress.retryRecords || []).map(record => [Number(record.__importRow), record]));
        const report = importProgress.outcomes.map(item => {
            const source = sourceByRow.get(Number(item.row)) || {};
            return {
                Row: item.row || '', Status: item.status, Reason: item.reason || '',
                'Part Number': item.partNumber || source.partNumber || source.part_number || '',
                Description: source.description || '', 'Noun/Modifier': source.nounModifier || source.noun_modifier || ''
            };
        });
        const ws = XLSX.utils.json_to_sheet(report);
        ws['!cols'] = [{ wch: 10 }, { wch: 12 }, { wch: 36 }, { wch: 24 }, { wch: 45 }, { wch: 28 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Import Results');
        XLSX.writeFile(wb, `noun_modifier_import_report_${Date.now()}.xlsx`);
    };

    /** Try to identify which column maps to which field based on header name */
    const detectColumnMapping = (headers) => {
        const lower = headers.map(h => (h || '').toString().toLowerCase().trim());
        const pnIdx = lower.findIndex(h => /^(part.?number|pn|part.?no|part_number|partno|partnumber)$/i.test(h));
        const descIdx = lower.findIndex(h => /^(description|desc|item.?desc|item.?description)$/i.test(h));
        const nounIdx = lower.findIndex(h => /^(noun.?modifier|noun|noun_modifier|noun.modifier|classification)$/i.test(h));
        const promptIdx = -1; // Prompt no longer managed here
        return { pnIdx, descIdx, nounIdx, promptIdx };
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true); setImportResult(null);
        try {
            let records = [];
            const ext = file.name.split('.').pop().toLowerCase();

            if (ext === 'xlsx' || ext === 'xls') {
                // ── Excel file ──────────────────────────────
                const data = await file.arrayBuffer();
                const wb = XLSX.read(data, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                if (raw.length < 2) throw new Error('File must have a header row and data rows');

                const headers = raw[0].map(h => (h || '').toString().trim());
                const colCount = headers.filter(Boolean).length;

                if (colCount === 1) {
                    // ── Single column: same value → Part Number, Description, Noun/Modifier
                    for (let i = 1; i < raw.length; i++) {
                        const val = (raw[i][0] || '').toString().trim();
                        if (!val) continue;
                        records.push({ partNumber: val, description: val, nounModifier: val });
                    }
                } else {
                    // ── Multi-column: auto-detect mapping
                    const { pnIdx, descIdx, nounIdx, promptIdx } = detectColumnMapping(headers);
                    // Fallback: col0=PN, col1=Desc, col2=Noun
                    const pn = pnIdx >= 0 ? pnIdx : 0;
                    const desc = descIdx >= 0 ? descIdx : (colCount > 1 ? 1 : 0);
                    const noun = nounIdx >= 0 ? nounIdx : (colCount > 2 ? 2 : 0);
                    const prm = promptIdx >= 0 ? promptIdx : -1;

                    for (let i = 1; i < raw.length; i++) {
                        const row = raw[i];
                        const pnVal = (row[pn] || '').toString().trim();
                        if (!pnVal) continue;
                        records.push({
                            partNumber: pnVal,
                            description: (row[desc] || '').toString().trim(),
                            nounModifier: (row[noun] || '').toString().trim(),
                            prompt: prm >= 0 ? (row[prm] || '').toString().trim() : ''
                        });
                    }
                }
            } else if (ext === 'csv' || ext === 'txt') {
                // ── CSV / TXT ───────────────────────────────
                const text = await file.text();
                const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
                if (lines.length < 2) throw new Error('File must have a header row and data rows');

                // RFC 4180 CSV line parser — respects quoted fields with commas inside
                const parseCSVLine = (line) => {
                    const fields = [];
                    let current = '';
                    let inQuotes = false;
                    for (let i = 0; i < line.length; i++) {
                        const ch = line[i];
                        if (inQuotes) {
                            if (ch === '"') {
                                if (i + 1 < line.length && line[i + 1] === '"') {
                                    current += '"';
                                    i++; // skip escaped double-quote
                                } else {
                                    inQuotes = false;
                                }
                            } else {
                                current += ch;
                            }
                        } else {
                            if (ch === '"') {
                                inQuotes = true;
                            } else if (ch === ',') {
                                fields.push(current.trim());
                                current = '';
                            } else {
                                current += ch;
                            }
                        }
                    }
                    fields.push(current.trim());
                    return fields;
                };

                const headers = parseCSVLine(lines[0]);
                const colCount = headers.filter(Boolean).length;

                if (colCount === 1) {
                    // Single column → same value for all fields
                    for (let i = 1; i < lines.length; i++) {
                        const val = parseCSVLine(lines[i])[0] || '';
                        if (!val) continue;
                        records.push({ partNumber: val, description: val, nounModifier: val });
                    }
                } else {
                    const { pnIdx, descIdx, nounIdx, promptIdx } = detectColumnMapping(headers);
                    const pn = pnIdx >= 0 ? pnIdx : 0;
                    const desc = descIdx >= 0 ? descIdx : (colCount > 1 ? 1 : 0);
                    const noun = nounIdx >= 0 ? nounIdx : (colCount > 2 ? 2 : 0);
                    const prm = promptIdx >= 0 ? promptIdx : -1;

                    for (let i = 1; i < lines.length; i++) {
                        const vals = parseCSVLine(lines[i]);
                        const pnVal = (vals[pn] || '').trim();
                        if (!pnVal) continue;
                        records.push({
                            partNumber: pnVal,
                            description: (vals[desc] || '').trim(),
                            nounModifier: (vals[noun] || '').trim(),
                            prompt: prm >= 0 ? (vals[prm] || '').trim() : ''
                        });
                    }
                }
            } else if (ext === 'json') {
                const text = await file.text();
                const parsed = JSON.parse(text);
                records = Array.isArray(parsed) ? parsed : (parsed.data || parsed.records || []);
            } else {
                throw new Error('Supported formats: .xlsx, .xls, .csv, .json');
            }

            if (records.length === 0) throw new Error('No records found in file');

            records = records.map((record, index) => ({
                ...record,
                __importRow: Number(record.__importRow) || index + 2
            }));

            // Confirm for very large imports
            if (records.length > 10000) {
                const ok = window.confirm(`This file contains ${records.length.toLocaleString()} records. Large imports may take a few minutes. Continue?`);
                if (!ok) { setImporting(false); e.target.value = ''; return; }
            }

            await runBulkImport(records);
        } catch (err) { toast.error(err.message || 'Import failed'); }
        finally { setImporting(false); e.target.value = ''; }
    };

    // ── Export ────────────────────────────────────────────────
    const handleExport = async (format) => {
        setExporting(true);
        try {
            const resp = await apiClient.nounModifierExport(searchTerm);
            const data = resp.data || [];
            if (data.length === 0) { toast.warning('No data to export'); return; }

            if (format === 'xlsx') {
                const wsData = [['Part Number', 'Description', 'Noun/Modifier']];
                data.forEach(r => wsData.push([r.part_number, r.description || '', r.noun_modifier || '']));
                const ws = XLSX.utils.aoa_to_sheet(wsData);
                ws['!cols'] = [{ wch: 25 }, { wch: 50 }, { wch: 25 }];
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'NounModifier');
                XLSX.writeFile(wb, `noun_modifier_export_${Date.now()}.xlsx`);
            } else if (format === 'csv') {
                const header = 'Part Number,Description,Noun/Modifier\n';
                const rows = data.map(r => `"${r.part_number}","${(r.description || '').replace(/"/g, '""')}","${r.noun_modifier || ''}"`).join('\n');
                const blob = new Blob([header + rows], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `noun_modifier_export_${Date.now()}.csv`; a.click();
                URL.revokeObjectURL(url);
            } else {
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `noun_modifier_export_${Date.now()}.json`; a.click();
                URL.revokeObjectURL(url);
            }
            toast.success(`Exported ${data.length.toLocaleString()} records as ${format.toUpperCase()}`);
        } catch (err) { toast.error(err.message || 'Export failed'); }
        finally { setExporting(false); }
    };

    // ── Page range logic ─────────────────────────────────────
    const pageNums = useMemo(() => {
        const t = pagination.totalPages, c = pagination.page;
        if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);
        if (c <= 3) return [1, 2, 3, 4, '...', t];
        if (c >= t - 2) return [1, '...', t - 3, t - 2, t - 1, t];
        return [1, '...', c - 1, c, c + 1, '...', t];
    }, [pagination.page, pagination.totalPages]);

    return (
        <div className="max-w-7xl mx-auto space-y-4">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.json,.txt" className="hidden" onChange={handleFileChange} />

            {/* ══ HEADER ═══════════════════════════════════════ */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-500 flex items-center justify-center shadow-lg shadow-violet-200">
                        <Tag className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-extrabold text-slate-900">Noun / Modifier Master</h1>
                        <p className="text-xs text-slate-500">Manage part classifications • {stats.total.toLocaleString()} groups • {(stats.totalItems || 0).toLocaleString()} items • {stats.uniqueNouns.toLocaleString()} unique nouns</p>
                    </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                    <button onClick={() => { resetForm(); setFormOpen(f => !f); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition shadow-sm">
                        <Plus className="w-4 h-4" /> Add
                    </button>
                    <button onClick={handleImportClick} disabled={importing}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition shadow-sm disabled:opacity-50">
                        {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
                    </button>
                    <div className="relative group">
                        <button disabled={exporting}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition shadow-sm disabled:opacity-50">
                            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export <ChevronDown className="w-3 h-3" />
                        </button>
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 min-w-[120px]">
                            <button onClick={() => handleExport('csv')} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition">📄 Export CSV</button>
                            <button onClick={() => handleExport('json')} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition">📋 Export JSON</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══ IMPORT RESULT BANNER ════════════════════════ */}
            {importResult && (
                <div className="flex items-center gap-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm animate-in slide-in-from-top-2">
                    <Check className="w-5 h-5 text-blue-600 shrink-0" />
                    <span className="text-blue-800">
                        <strong>Import complete:</strong> {importResult.inserted} inserted, {importResult.updated} updated, {importResult.skipped} skipped
                        {importResult.errors?.length > 0 && <span className="text-red-600"> • {importResult.errors.length} error(s)</span>}
                    </span>
                    <button onClick={() => setImportResult(null)} className="ml-auto text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
                </div>
            )}

            {/* ══ FULL-SCREEN RECORD EDITOR ═════════════════ */}
            {importProgress?.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">{importProgress.title}</h2>
                                <p className="mt-1 text-sm text-slate-500">{importProgress.running ? 'Import is running. Follow each batch below.' : 'Import completed. Retry or export problem rows.'}</p>
                            </div>
                            {!importProgress.running && <button onClick={() => setImportProgress(p => ({ ...p, open: false }))} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>}
                        </div>
                        <div className="space-y-5 px-6 py-5">
                            <div>
                                <div className="mb-2 flex justify-between text-sm font-medium text-slate-700">
                                    <span>{importProgress.processed.toLocaleString()} of {importProgress.total.toLocaleString()} processed</span>
                                    <span>{importProgress.total ? Math.round(importProgress.processed / importProgress.total * 100) : 0}%</span>
                                </div>
                                <div className="h-3 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-blue-600 transition-all duration-300" style={{ width: `${importProgress.total ? importProgress.processed / importProgress.total * 100 : 0}%` }} /></div>
                                <p className="mt-2 text-xs text-slate-500">Remaining: {Math.max(0, importProgress.total - importProgress.processed).toLocaleString()}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                {[
                                    ['Imported', importProgress.inserted + importProgress.updated, 'text-emerald-700 bg-emerald-50 border-emerald-200'],
                                    ['Inserted', importProgress.inserted, 'text-blue-700 bg-blue-50 border-blue-200'],
                                    ['Skipped', importProgress.skipped, 'text-amber-700 bg-amber-50 border-amber-200'],
                                    ['Failed', importProgress.failed, 'text-red-700 bg-red-50 border-red-200']
                                ].map(([label, value, colors]) => <div key={label} className={`rounded-xl border p-3 ${colors}`}><div className="text-xs font-semibold uppercase">{label}</div><div className="mt-1 text-2xl font-bold">{Number(value).toLocaleString()}</div></div>)}
                            </div>
                            {importProgress.outcomes.some(item => item.status === 'skipped' || item.status === 'failed') && (
                                <div className="max-h-48 overflow-auto rounded-xl border border-slate-200">
                                    <table className="w-full text-left text-xs">
                                        <thead className="sticky top-0 bg-slate-50 text-slate-600"><tr><th className="px-3 py-2">Row</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Part Number</th><th className="px-3 py-2">Reason</th></tr></thead>
                                        <tbody className="divide-y divide-slate-100">{importProgress.outcomes.filter(item => item.status === 'skipped' || item.status === 'failed').map((item, index) => <tr key={`${item.row}-${item.status}-${index}`}><td className="px-3 py-2">{item.row}</td><td className={`px-3 py-2 font-semibold ${item.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>{item.status}</td><td className="px-3 py-2">{item.partNumber || '-'}</td><td className="px-3 py-2 text-slate-600">{item.reason || '-'}</td></tr>)}</tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
                            <button onClick={exportImportReport} disabled={!importProgress.outcomes.length} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40"><Download className="h-4 w-4" /> Export report</button>
                            <button onClick={() => retryImportOutcomes(['skipped'])} disabled={importProgress.running || !importProgress.skipped} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-800 disabled:opacity-40"><RefreshCw className="h-4 w-4" /> Retry skipped</button>
                            <button onClick={() => retryImportOutcomes(['failed'])} disabled={importProgress.running || !importProgress.failed} className="inline-flex items-center gap-1.5 rounded-lg bg-red-100 px-3 py-2 text-sm font-semibold text-red-700 disabled:opacity-40"><RefreshCw className="h-4 w-4" /> Retry failed</button>
                            {!importProgress.running && <button onClick={() => setImportProgress(p => ({ ...p, open: false }))} className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white">Close</button>}
                        </div>
                    </div>
                </div>
            )}

            <NMRecordEditor
                open={formOpen}
                editingId={editingId}
                initialData={form}
                onClose={() => { setFormOpen(false); resetForm(); }}
                onEditIdChange={(newId) => setEditingId(newId)}
                onSave={async (data) => {
                    try {
                        if (editingId) {
                            await apiClient.nounModifierUpdate(editingId, data);
                            toast.success('Updated successfully');
                        } else {
                            const result = await apiClient.nounModifierCreate(data);
                            toast.success(`Created group with ${result.count} record(s)`);
                        }
                        resetForm();
                        setFormOpen(false);
                        fetchRecords(editingId ? pagination.page : 1);
                        fetchStats();
                    } catch (err) {
                        toast.error(err.message || 'Save failed');
                        throw err;
                    }
                }}
            />

            {/* ══ TOOLBAR ══════════════════════════════════════ */}
            <div className="card !py-3 !px-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                        {/* Search */}
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search part number, description, noun..."
                                className="input-field !pl-9 !py-2 text-sm !rounded-lg" />
                            {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>}
                        </div>

                        {/* Bulk actions */}
                        {selectedIds.size > 0 && (
                            <div className="flex items-center gap-2 animate-in slide-in-from-left-2">
                                <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-lg">{selectedIds.size} selected</span>
                                <button onClick={handleBulkDelete}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold rounded-lg transition">
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                                <button onClick={() => setSelectedIds(new Set())} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Rows per page */}
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <span>Rows:</span>
                            <select value={pagination.limit}
                                onChange={(e) => {
                                    const newLimit = Number(e.target.value);
                                    setPagination(p => { const updated = { ...p, limit: newLimit }; paginationRef.current = updated; return updated; });
                                    setTimeout(() => fetchRecords(1), 0);
                                }}
                                className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-100">
                                {[50, 100, 200, 500, 1000].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>

                        <button onClick={() => fetchRecords(pagination.page)} className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 transition" title="Refresh">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>

                        <span className="text-xs text-slate-500 hidden sm:inline whitespace-nowrap">
                            {pagination.total.toLocaleString()} total{debouncedSearch && ` (filtered)`}
                        </span>
                    </div>
                </div>
            </div>

            {/* ══ DATA TABLE ═══════════════════════════════════ */}
            <div className="card !p-0 overflow-hidden border border-slate-200/80">
                <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10">
                            <tr className="bg-slate-50/95 backdrop-blur-sm border-b border-slate-200">
                                <th className="px-3 py-2.5 w-10">
                                    <button onClick={toggleAll} className="text-slate-400 hover:text-blue-600 transition">
                                        {allSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                                    </button>
                                </th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-12">
                                    <button onClick={() => handleSort('id')} className="flex items-center gap-1 hover:text-slate-800 transition"># <SortIcon col="id" /></button>
                                </th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-56">
                                    Part Numbers
                                </th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                    Descriptions
                                </th>
                                <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-52">
                                    <button onClick={() => handleSort('noun_modifier')} className="flex items-center gap-1 hover:text-slate-800 transition">Noun/Modifier <SortIcon col="noun_modifier" /></button>
                                </th>
                                <th className="px-3 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase tracking-wider w-20">Act</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/80">
                            {loading ? (
                                <tr><td colSpan={6} className="px-4 py-20 text-center">
                                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
                                    <p className="text-sm text-slate-400">Loading...</p>
                                </td></tr>
                            ) : records.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-20 text-center">
                                    <Database className="w-8 h-8 text-slate-200 mx-auto mb-3" />
                                    <p className="text-sm text-slate-500 font-medium">{debouncedSearch ? 'No records match' : 'No records yet'}</p>
                                    <p className="text-xs text-slate-400 mt-1">{debouncedSearch ? 'Try a different search' : 'Click "Add" or "Import" to get started'}</p>
                                </td></tr>
                            ) : records.map((group, idx) => {
                                const isSelected = selectedIds.has(group.id);
                                const items = group.items || [];
                                const firstPN = items[0]?.part_number || '—';
                                const firstDesc = items[0]?.description || '';
                                const extraCount = items.length - 1;
                                return (
                                    <tr key={group.id}
                                        className={`transition-colors ${isSelected ? 'bg-blue-50/60' : editingId === group.id ? 'bg-amber-50' : 'hover:bg-slate-50/60'}`}>
                                        <td className="px-3 py-2">
                                            <button onClick={() => toggleOne(group.id)} className="text-slate-400 hover:text-blue-600 transition">
                                                {isSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2 text-[11px] text-slate-400 font-mono">{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <code className="text-xs font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{firstPN}</code>
                                                {extraCount > 0 && (
                                                    <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-md border border-indigo-100" title={items.map(it => it.part_number).join(', ')}>+{extraCount}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-slate-600 max-w-md">
                                            <div className="flex items-center gap-1.5">
                                                <span className="truncate" title={firstDesc}>{firstDesc || <span className="text-slate-300">—</span>}</span>
                                                {extraCount > 0 && (
                                                    <span className="shrink-0 text-[10px] font-bold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-md border border-teal-100">+{extraCount}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="inline-block px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-xs font-semibold border border-violet-100">
                                                {group.noun_modifier || '—'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <div className="flex items-center justify-center gap-0.5">
                                                <button onClick={() => handleEdit(group)} className="p-1 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                                                <button onClick={() => setDeleteModal(group)} className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ──────────────────────────────── */}
                {pagination.totalPages > 0 && (
                    <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50/50">
                        <div className="text-[11px] text-slate-500">
                            <span className="font-semibold text-slate-700">{((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-semibold text-slate-700">{pagination.total.toLocaleString()}</span>
                            <span className="ml-2 text-slate-400">({pagination.totalPages.toLocaleString()} pages)</span>
                        </div>
                        <div className="flex items-center gap-1">
                            {/* First page */}
                            <button onClick={() => fetchRecords(1)} disabled={pagination.page <= 1}
                                className="px-1.5 py-1 rounded-lg text-[10px] font-semibold text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition">First</button>
                            <button onClick={() => fetchRecords(pagination.page - 1)} disabled={pagination.page <= 1}
                                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition"><ChevronLeft className="w-4 h-4" /></button>

                            {pageNums.map((p, i) => (
                                p === '...' ? <span key={`e${i}`} className="px-1 text-xs text-slate-400">…</span> : (
                                    <button key={p} onClick={() => fetchRecords(p)}
                                        className={`min-w-[28px] h-7 text-xs font-semibold rounded-lg transition ${p === pagination.page ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-200'}`}>{p}</button>
                                )
                            ))}

                            <button onClick={() => fetchRecords(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}
                                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition"><ChevronRight className="w-4 h-4" /></button>
                            {/* Last page */}
                            <button onClick={() => fetchRecords(pagination.totalPages)} disabled={pagination.page >= pagination.totalPages}
                                className="px-1.5 py-1 rounded-lg text-[10px] font-semibold text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition">Last</button>

                            {/* Go to page */}
                            {pagination.totalPages > 7 && (
                                <form onSubmit={(e) => { e.preventDefault(); const v = parseInt(e.target.goPage.value); if (v >= 1 && v <= pagination.totalPages) { fetchRecords(v); e.target.goPage.value = ''; } else toast.error(`Page must be 1–${pagination.totalPages}`); }}
                                    className="flex items-center gap-1 ml-2">
                                    <input name="goPage" type="number" min={1} max={pagination.totalPages} placeholder="Go to..."
                                        className="w-[72px] h-7 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                    <button type="submit" className="h-7 px-2 text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition">Go</button>
                                </form>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ══ DELETE MODAL ════════════════════════════════ */}
            {deleteModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-5 max-w-sm w-full mx-4 shadow-xl border border-slate-200">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-600" /></div>
                            <h3 className="text-base font-bold text-slate-900">Delete group?</h3>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2.5 mb-4 text-xs text-slate-600 space-y-0.5">
                            <p><span className="font-semibold">PNs:</span> {(deleteModal.items || []).map(it => it.part_number).join(', ') || '—'}</p>
                            <p><span className="font-semibold">Noun:</span> {deleteModal.noun_modifier || '—'}</p>
                            {(deleteModal.items || []).length > 1 && (
                                <p className="text-red-600 font-semibold mt-1">⚠ This will delete {deleteModal.items.length} Part Number(s)</p>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setDeleteModal(null)} className="flex-1 btn-secondary !py-2 text-sm">Cancel</button>
                            <button onClick={handleDelete} className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
